import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import * as pdu from 'pdu';
import { Observable, Subject, forkJoin } from 'rxjs';
import { map } from 'rxjs/operators';
import { DataCoding, DetectedDataCodingResponse } from '../models/data-coding.model';
import { Destination } from '../models/destination.model';
import { Network } from '../models/network.model';
import { PduDetails, SubmitSmRespPduData } from '../models/pdu-details.model';
import { SmppDelay } from '../models/smpp-delay.model';
import { SupplierSessionAssignment } from '../models/supplier-session-assignment.model';
import { TestGroupInfo } from '../models/test-group-info.model';
import { NPI_ITEMS, TON_ITEMS, TestGroup, getItemLabel } from '../models/test-group.model';
import { TestComment, TestDetails, TestResult, TestResultsCollection } from '../models/test-result.model';
import { UdhTlv } from '../models/udh-tlv.model';
import { BackendChannelService } from './backend-channel.service';
import { AllRequestParamsNetwork, NetInfoService } from './net-info.service';
import { RestUtils } from './rest-utils';
import { TemplatesService } from './templates.service';
import { TestResultUtils } from './test-result-utils';

declare const moment: any;

@Injectable()
export class LiveNumberTestingService {
  pdu = pdu;
  http: HttpClient;
  utils = new RestUtils();
  headers = new HttpHeaders();
  private actionSubject: Subject<LntActionData> = new Subject<LntActionData>();
  action$ = this.actionSubject.asObservable();

  static DEFAULT_PRIORITY = 1;
  static PRIORITY_OPTIONS = [0, 1, 2, 3];
  ELASTIC_FILTERS: string[] = ['searchByComment', 'searchByText', 'commentDate'];

  constructor(
    http: HttpClient,
    private netInfoService: NetInfoService,
    private backendChannel: BackendChannelService
  ) {
    this.http = http;
    this.headers = this.headers.set('Content-Type', 'application/json');
  }

  announceAction(action: LntActionData) {
    this.actionSubject.next(action);
  }

  one(id: number): Observable<TestResult> {
    const url = this.utils.buildUrl('ROLE/ntc/test-results/' + id, {});
    const options = this.utils.getHttpHeaderOptions(this.headers);

    return this.http.get(url, options).pipe(
      map((_: TestResult) => {
        return TestResultUtils.prepareTestResult(_);
      })
    );
  }

  testGroupInfo(testGroupId: number): Observable<TestGroupInfo> {
    const url = this.utils.buildUrl(`ROLE/ntc/test-groups/${testGroupId}/unified-info`, {});
    const options = this.utils.getHttpHeaderOptions(this.headers);

    return this.http.get<TestGroupInfo>(url, options).pipe(
      map(info => {
        if (info.contentTexts && info.contentTexts.length) {
          info.contentTexts.forEach(ct => (ct.fromServer = true));
        }
        return info;
      })
    );
  }

  encodings(): Observable<DataCoding[]> {
    const url = this.utils.buildUrl('ROLE/ntc/encodings', {});
    const options = this.utils.getHttpHeaderOptions(this.headers);

    const toHex = d => {
      return ('0' + Number(d).toString(16)).slice(-2).toUpperCase();
    };

    return this.http.get<DataCoding[]>(url, options).pipe(
      map(encodings => {
        return encodings.map(_ => {
          _.dataCodingHex = '0x' + toHex(_.dataCoding);
          return _;
        });
      })
    );
  }

  detectEncoding(text: string): Observable<DataCoding> {
    const url = this.utils.buildUrl('ROLE/ntc/encodings/detect', {});
    const options = this.utils.getHttpHeaderOptions(this.headers);
    return this.http.post<DetectedDataCodingResponse>(url, { text: text }, options).pipe(map(_ => _.encoding));
  }

  results(params: AllRequestParams): Observable<TestResultsCollection> {
    const queryParams = {
      page: params.page,
      size: params.size,
      sort: ['id,desc'],
      onlyMyTests: params.onlyMyTests,
    };

    const url = this.utils.buildUrl('ROLE/search/test-results/search', queryParams);
    const options = this.utils.getHttpHeaderOptions(this.headers);

    return this.http.post<TestResultsCollection>(url, {}, options).pipe(
      map(response => {
        response.content = response.content.map(res => TestResultUtils.prepareTestResult(res));
        return response;
      })
    );
  }

  shouldRunOnElasticSearch(searchParams: SearchParams): boolean {
    const { commentText, commentDateFrom, commentDateTo, text } = searchParams;

    const isNonEmptyString = (value: string | null | undefined): boolean => value !== null && value !== undefined && value.length > 0;

    return isNonEmptyString(commentText) || isNonEmptyString(commentDateFrom) || isNonEmptyString(commentDateTo) || isNonEmptyString(text);
  }

  searchResults(params: AllRequestParams, searchParams: SearchParams): Observable<TestResultsCollection> {
    const queryParams = {
      page: params.page,
      size: params.size,
      sort: ['id,desc'],
      searchType: this.shouldRunOnElasticSearch(searchParams) ? 'ES' : 'BASIC',
    };
    queryParams.sort = ['id,desc'];
    const url = this.utils.buildUrl('ROLE/search/test-results/search', queryParams);

    for (const i in queryParams) {
      searchParams[i] = queryParams[i];
    }

    const options = this.utils.getHttpHeaderOptions(this.headers);

    return this.http.post<TestResultsCollection>(url, searchParams, options).pipe(
      map(_ => {
        const response = _;
        response.content = response.content.map(res => TestResultUtils.prepareTestResult(res));
        return response;
      })
    );
  }

  run(group: CreateTestGroup): Observable<any> {
    const url = this.utils.buildUrl(`ROLE/ntc/test-groups`, {});
    const options = this.utils.getHttpHeaderOptions(this.headers);

    return this.http.post(url, group, options);
  }

  recentDestinations(): Observable<Destination[]> {
    const params = new AllRequestParamsNetwork();
    params.light = true;
    return this.netInfoService.networks(params).pipe(map((networks: Network[]) => networks.map((net: Network) => this.mapNetworkToDestination(net))));
  }

  private mapNetworkToDestination(network: Network): Destination {
    return {
      mcc: network.mcc,
      mnc: network.mnc,
      providerName: network.name,
      countryName: network.country?.name,
    };
  }

  parsePdu(pdu: string) {
    return this.pdu.parse(pdu, true);
  }

  getResultDetailsText(result: TestResult): Observable<TestDetails[]> {
    return forkJoin([this.pduDetails(result.id), this.oneTestGroup(result.testGroupId), this.encodings()]).pipe(
      map(data => {
        let text = [];
        const pdu: PduDetails = data[0];
        const group: TestGroup = data[1];
        const encodings = data[2].map(_ => {
          return { id: _.id, label: _.name };
        });
        const submitSmRespPduData = pdu.submitSmRespPduData;
        const testCaseSummary = [];
        const isFlashCallTest = !!(result.flashCallDetection || group.flashCallDetection);

        // General ...
        text.push('ID: ' + result.id);
        text.push('Test group ID: ' + group.id);
        if (result.taskId) {
          text.push('Scheduled task ID: ' + result.taskId);
        }
        text.push('Date: ' + moment(result.createdAt).format('DD MMM YYYY HH:mm:ss'));

        if (submitSmRespPduData && result.messageState === 'SUBMIT_SM_FAILED') {
          text.push(`DLR status: ${result.messageState} (${this.getTitleForCommandStatus(submitSmRespPduData)})`);
          submitSmRespPduData.forEach((_, i) => {
            const commandStatus = _.commandStatus;
            text.push(`:<div class="text-neutral-n7">SUBMIT_SM Response #${i + 1}</div>`);
            text.push(`:<div class="text-neutral-n7">Command Status: ${commandStatus}</div>`);
            text.push(`:<div class="text-neutral-n7">PDU: ${_.pdu}</div>`);
          });
        } else {
          text.push('DLR status: ' + result.messageState);
        }
        text.push('DLR delay: ' + result.smppDelay + ' s');
        text.push('Rec. status: ' + result.testStatus);
        text.push('Rec. delay: ' + result.receiptDelay + ' s');
        if (TestResultUtils.isFlashCallDelivered(result)) {
          text.push('Delivered as: Flash call');
        }
        text.push('Sender sent: ' + result.senderSent);
        text.push('Sender delivered: ' + result.senderDelivered);
        if (result.flashCallCallerId) {
          text.push('Caller ID: ' + TestResultUtils.highlightTestIdInCallerId(result.flashCallCallerId, result.telqId));
        }
        text.push('Text sent: ' + result.textSent);
        text.push('Text delivered: ' + result.textDeliveredFull);
        text.push('Phone: ' + result.destinationDto.phone);
        if (result.smscInfoDto) {
          text.push('SMSC: ' + result.smscInfoDto.phone);
        }
        if (group.callerIp) {
          text.push('IP (Browser/Client): ' + group.callerIp);
        }
        testCaseSummary.push({
          id: 'testoverview',
          title: 'Test Overview',
          data: text,
        });
        text = [];

        // Test settings ...
        text.push('Country: ' + TestResultUtils.renderDestination(result.destinationDto));
        text.push('Supplier: ' + result.supplierDto.title + ' ' + result.supplierDto.routeType);
        let testIdTextType = TemplatesService.getLabelType(result.telqIdType);
        if (TemplatesService.showCaseForType(result.telqIdType)) {
          testIdTextType += `, ${TemplatesService.getLabelCase(result.telqIdCase)}`;
        }
        if (TemplatesService.showLengthForType(result.telqIdType)) {
          testIdTextType += `, ${result.telqIdLength}`;
        }
        text.push('Test ID text: ' + testIdTextType);
        const flashCallTtlNote = isFlashCallTest ? ' <span class="text-neutral-n7">(set by flash call detection)</span>' : '';
        if (group.ttl && group.ttl % 60 === 0) {
          text.push('TTL: ' + parseInt(String(group.ttl / 60)) + ' min' + flashCallTtlNote);
        } else {
          text.push('TTL: ' + group.ttl + ' sec' + flashCallTtlNote);
        }
        if (isFlashCallTest) {
          text.push('Flash call detection: Enabled');
        }
        testCaseSummary.push({
          id: 'testsettings',
          title: 'Test settings',
          data: text,
        });
        text = [];

        if (group.tlvDtos?.length) {
          group.tlvDtos.forEach((td, index) => {
            text.push(`TLV Tag ${index + 1}: ${td.tagHex}`);
            text.push(`TLV Value ${index + 1}: ${td.valueHex}`);
          });
        }
        if (group.udhDtos?.length) {
          group.udhDtos.forEach((td, index) => {
            text.push(`UDH Tag ${index + 1}: ${td.tagHex}`);
            text.push(`UDH Value ${index + 1}: ${td.valueHex}`);
          });
        }
        text.push('Protocol ID: ' + (group.protocolId !== null ? group.protocolId : 'Automatic'));
        text.push('Source TON: ' + getItemLabel(TON_ITEMS, group.srcTon));
        text.push('Source NPI: ' + getItemLabel(NPI_ITEMS, group.srcNpi));
        text.push('Data Coding: ' + getItemLabel(encodings, group.encodingId));
        const validityPeriod = group.validityPeriod ? group.validityPeriod : 'Automatic';
        text.push('Validity Period: ' + validityPeriod);
        const scheduleDeliveryTime = group.scheduleDeliveryTime ? group.scheduleDeliveryTime : 'Automatic';
        text.push('Scheduled Delivery Time: ' + scheduleDeliveryTime);
        const replaceIfPresent = group.replaceIfPresent ? true : false;
        text.push('Replace If Present: ' + replaceIfPresent);
        const priority = group.priority ?? LiveNumberTestingService.DEFAULT_PRIORITY;
        text.push('Priority Flag: ' + priority);

        testCaseSummary.push({
          id: 'smppsettings',
          title: 'SMPP settings',
          data: text,
        });
        text = [];

        if (pdu) {
          const smppMap = {};
          const pduMap = {};
          const decodedPduMap = {};
          text.push('');
          (pdu.submitSmPduData ? pdu.submitSmPduData : []).map((_, i) => {
            text.push('SMPP PACKET #' + (i + 1));
            text.push('Message ID: ' + _.messageId);
            text.push('Sent at: ' + moment(_.submittedAt).format('DD MMM YYYY HH:mm:ss'));
            if (pdu.deliveredSmsPduData && pdu.deliveredSmsPduData.pdusDelivered[i] && pdu.deliveredSmsPduData.pdusDelivered[i].length) {
              text.push('Delivered at: ' + moment(pdu.deliveredSmsPduData.deliveredAt).format('DD MMM YYYY HH:mm:ss'));
              smppMap[i] = text;
              text = [];
              // PDU ...
              text.push('PDU: ' + pdu.deliveredSmsPduData.pdusDelivered[i]);
              pduMap[i] = text;
              text = [];
              try {
                const decodedPdu = this.pdu.parse(pdu.deliveredSmsPduData.pdusDelivered[i], true);
                text.push('SMSC: ' + decodedPdu['smsc']);
                text.push('SMSC Type: ' + decodedPdu['smsc_type']);
                text.push('Sender ID: ' + decodedPdu['sender']);
                text.push('Sender ID Type: ' + decodedPdu['sender_type']);
                text.push('Encoding: ' + decodedPdu['encoding']);
                text.push('Timestamp: ' + decodedPdu['time']);
                text.push('Text: ' + decodedPdu['text']);

                if (decodedPdu['udh'] == null) {
                  text.push('  * No UDH is present in the PDU * ');
                } else {
                  text.push('UDH in hex: ' + decodedPdu['udh_hex']);
                  text.push('UDH length: ' + decodedPdu['udh']['length']);
                  text.push('UDH IEI: ' + decodedPdu['udh']['iei']);
                  text.push('UDH Header Length: ' + decodedPdu['udh']['header_length']);
                  text.push('UDH Reference Number: ' + decodedPdu['udh']['reference_number']);
                  text.push('UDH Parts: ' + decodedPdu['udh']['parts']);
                  text.push('UDH Current part: ' + decodedPdu['udh']['current_part']);
                }
                decodedPduMap[i] = text;
                text = [];
              } catch (e) {
                console.error(e);
              }
            } else {
              smppMap[i] = text;
              text = [];
            }
          });
          if (Object.keys(smppMap).length > 0) {
            testCaseSummary.push({
              id: 'smpp',
              title: 'SMPP',
              data: [].concat.apply([], Object.values(smppMap)),
            });
          }

          if (Object.keys(pduMap).length > 0) {
            testCaseSummary.push({
              id: 'pdu',
              title: 'PDU',
              data: [].concat.apply([], Object.values(pduMap)),
            });
          }
          if (Object.keys(decodedPduMap).length > 0) {
            testCaseSummary.push({
              id: 'decodedPdu',
              title: 'Decoded delivered PDU',
              data: [].concat.apply([], Object.values(decodedPduMap)),
            });
          }
        }
        return testCaseSummary;
      })
    );
  }

  oneTestGroup(testGroupId): Observable<TestGroup> {
    const url = this.utils.buildUrl(`ROLE/ntc/test-groups/${testGroupId}`);
    const options = this.utils.getHttpHeaderOptions(this.headers);

    return this.http.get<TestGroup>(url, options);
  }

  pduDetails(testId: number): Observable<PduDetails> {
    const url = this.utils.buildUrl(`ROLE/ntc/tests/${testId}/pdu-details`);
    const options = this.utils.getHttpHeaderOptions(this.headers);

    return this.http.get<PduDetails>(url, options);
  }

  getSmppDelayByTestId(testId: number): Observable<SmppDelay> {
    const url = this.utils.buildUrl(`ROLE/ntc/test-delay/${testId}`, {});
    const options = this.utils.getHttpHeaderOptions(this.headers);

    return this.http.get<SmppDelay>(url, options);
  }

  getFileFromExportResult(result) {
    // split by "/" and return last item
    return result.filePath.split('/').slice(-1);
  }

  createComment(testId: number, model: TestComment): Observable<any> {
    const url = this.utils.buildUrl(`ROLE/ntc/tests/${testId}/comment`);
    const options = this.utils.getHttpHeaderOptions(this.headers);

    return this.http.post(url, model, options);
  }

  updateComment(commentId: number, model: TestComment): Observable<any> {
    const url = this.utils.buildUrl(`ROLE/ntc/tests/comment/${commentId}`);
    const options = this.utils.getHttpHeaderOptions(this.headers);

    return this.http.put(url, model, options);
  }

  reimburse(testIds: number[]): Observable<void> {
    const url = this.utils.buildUrl(`ROLE/ntc/tests/release-credits`);
    const options = this.utils.getHttpHeaderOptions(this.headers);

    return this.http.post<void>(url, testIds, options);
  }

  getCommentById(testId: number, commentId: number): Observable<TestComment> {
    const url = this.utils.buildUrl(`ROLE/ntc/tests/${testId}/comment/${commentId}`);
    const options = this.utils.getHttpHeaderOptions(this.headers);

    return this.http.get<TestComment>(url, options);
  }

  exportDestinations(requestBody: object = {}): Promise<ExportResult> {
    return new Promise((resolve, reject) => {
      const options = this.utils.getHttpHeaderOptions(this.headers);
      this.http.post<ExportResult>(this.utils.buildUrl('ROLE/exp/export-async/active-networks'), requestBody, options).subscribe(d => {
        const statusCallBackUrl = d.statusCallback;
        let repeat = 0;
        const maxRepeat = 12;
        const interval = setInterval(() => {
          const options = this.utils.getHttpHeaderOptions(this.headers);
          this.http.get<ExportStatus>(this.utils.buildUrl(`ROLE/exp/${statusCallBackUrl}`), options).subscribe(response => {
            if (response.status === 'EXPORTED') {
              clearInterval(interval);
              d.downloadCallback = this.backendChannel.prepareUrl(this.utils.buildUrl(`ROLE/exp${d.downloadCallback}`));
              resolve(d);
            }
            repeat++;
            if (['FAILED', 'EXPIRED'].includes(response.status)) {
              clearInterval(interval);
              reject({ response, exportDetails: null });
            }
            if (maxRepeat === repeat) {
              clearInterval(interval);
              reject({ response, exportDetails: null });
            }
          });
        }, 3000);
      });
    });
  }

  getTitleForCommandStatus(submitSmRespPduData: SubmitSmRespPduData[]): string {
    let results = '';
    submitSmRespPduData.forEach((_, i) => {
      const pduString = _.pdu;
      if (pduString) {
        const resultRegex = /result:\s"([^"]*)"/;
        const resultMatch = pduString.match(resultRegex);
        results += resultMatch ? resultMatch[1] : 'Unknown Error';
        if (i < submitSmRespPduData.length - 1) {
          results += ', ';
        }
      }
    });
    return results;
  }

  create() {
    return {
      id: null,
      enabled: true,
      countries: [],
      connections: [],
      senders: [],
      templates: [],
      dcs: -1,
      ton: -1,
      pid: null,
      npi: -1,
      ttl_min: 60,
      ttl: 3600,
      testRepeatsPerIteration: 1,
      amountX: 1,
      commentText: '',
      validityPeriod: null,
      scheduleDeliveryTime: null,
      replaceIfPresent: false,
      priority: LiveNumberTestingService.DEFAULT_PRIORITY,
      textAsTlv: false,
    };
  }
}

export interface CreateTestGroup {
  senders?: string[];
  dynamicTexts?: TelqText[];
  contentTexts: any[];
  sessionsSuppliers: SupplierSessionAssignment[];
  destinations: Destination[];
  tlv: UdhTlv[];
  udh: UdhTlv[];
  encodingId?: number;
  protocolId?: number;
  srcNpi?: number;
  srcTon?: number;
  userId: number;
  testRepeatsPerIteration: number;
  amountX: number;
  commentText?: string;
  ttl?: number;
  validityPeriod?: number;
  scheduleDeliveryTime?: string;
  replaceIfPresent?: boolean;
  priority?: number;
  useRcsNumbers?: boolean;
  flashCallDetection?: boolean;
}

export interface TelqText {
  id?: number;
  fromServer?: boolean;
  text: string;
  telqIdType: string;
  telqIdText: string;
  telqIdCase: string;
  telqIdLength: number;
}

export class AllRequestParams {
  size = 20;
  page = 1;
  search: string;
  sort: string[] = [];
  onlyMyTests = false;

  setSort(propertyName: string, direction: string) {
    this.sort.push(propertyName + (direction === 'desc' ? ',desc' : ''));
  }

  removeSort(propertyName: string) {
    this.sort = this.sort.filter(_ => _.indexOf(propertyName) === -1);
  }

  resetSort() {
    this.sort = [];
  }
}

export interface SearchParams {
  createdAtFrom?: string;
  createdAtTo?: string;
  destinations?: Destination[];
  ids?: string[];
  telqIds?: string[];
  messageStates?: string[];
  senders?: string[];
  sessionSupplierIds?: SupplierSessionAssignment[];
  testStatuses?: string[];
  userIds?: number[];
  taskId?: number;
  taskIds?: number[];
  commentText?: string;
  commentDateFrom?: string;
  commentDateTo?: string;
  usersCollection?: any;
  text?: any;
  tlv?: { tagHex: string; valueHex: string };
  testGroupIds?: string[];
  showOnlyScheduledTests?: boolean;
  channels?: string[];
}

interface ExportStatus {
  status: string;
}
interface ExportResult {
  statusCallback: string;
  filePath: string;
  downloadCallback: string;
}

interface LntActionData {
  name: string;
  row: TestResult;
  column: string;
}
